<script>
import Fa from "svelte-fa"
import { faStar as emptyStar } from "@fortawesome/free-regular-svg-icons"
import {
  faStar as solidStar,
  faPeopleGroup,
  faArrowUpRightFromSquare,
  faCalendar
} from "@fortawesome/free-solid-svg-icons"

import backend from "~/backend"
import ImageUpload from "~/components/ImageUpload.svelte"
import Gallery from "~/components/Gallery.svelte"

import Ingredients from "./Ingredients.svelte"
import Instructions from "./Instructions.svelte"
import Notes from "./Notes.svelte"

export let initialData

let uuid = 0

let { recipe } = initialData
let {
  id,
  recipe_id,
  links,
  gallery,
  ingredients,
  instructions,
  rating,
  notes,
  original_servings,
  servings
} = recipe

function getDisplayUrl(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "")
  } catch {
    return url
  }
}

const csrfToken =
  document.querySelector('meta[name="csrf-token"]')?.content || ""

let timer
const debouncedUpdate = () => {
  clearTimeout(timer)
  timer = setTimeout(() => {
    persistChanges()
  }, 500)
}

function persistChanges() {
  const payload = {
    start_time: recipe.start_time,
    rating: rating,
    notes: notes,
    servings: servings,
    ingredients: ingredients.map((group) => {
      return {
        name: group.name,
        ingredients: group.ingredients
      }
    }),
    instructions: instructions.map((group) => {
      return {
        name: group.name,
        steps: group.steps
      }
    })
  }
  backend.updateRecipePreparation(recipe.id, recipe.recipe_id, {
    recipe: payload
  })
}

function confirmDelete(event) {
  if (!window.confirm("Are you sure?")) {
    event.preventDefault()
  }
}

$: debouncedUpdate(recipe, ingredients, instructions, rating, notes, servings)
</script>

<main class="flex flex-col md:flex-row md:space-x-10">
  <div class="flex flex-col md:w-9/12 gap-2">
    <div class="flex flex-col md:flex-row gap-1 md:items-center">
      <div class="grow py-1">
        <a href="/recipes/{recipe_id}" class="text-2xl font-normal">{recipe.name}</a>
      </div>

      <div class="flex flex-row-reverse md:flex-row justify-between gap-2">
        <form
          class="inline-block"
          method="post"
          action={`/recipes/${recipe_id}/preparations/${id}`}
          on:submit={confirmDelete}
          data-turbo="false"
        >
          <input type="hidden" name="_method" value="delete" />
          <input type="hidden" name="authenticity_token" value={csrfToken} />
          <button type="submit" class="btn btn-link-danger"><i class="fa-regular fa-trash-can text-xs"></i> Delete</button>
        </form>
        <form
          class="inline-block"
          method="post"
          action={`/recipes/${recipe_id}/preparations/${id}/duplicate`}
          data-turbo="false"
        >
          <input type="hidden" name="authenticity_token" value={csrfToken} />
          <button type="submit" class="btn btn-primary">Make again</button>
        </form>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm border-b border-t
      border-border py-1">
      <div class="flex items-center gap-1.5">
        <Fa icon={faCalendar} class="text-xs text-fg-muted" />
        <span class="text-fg-muted">Cooking date</span>
        <input name="start_time" type="datetime-local" bind:value={recipe.start_time} class="date-input" />
      </div>
      <div class="flex items-center gap-1.5">
        <Fa icon={faPeopleGroup} class="text-xs text-fg-muted" />
        <span class="text-fg-muted">Servings</span>
        <input type="number" placeholder="—" bind:value={servings} class="servings-input" />
      </div>
    </div>

    {#if links && links.length > 0}
      <div class="mt-1 flex flex-wrap items-center gap-2 text-sm">
        {#each links as link}
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1.5 px-2 py-0.5 bg-slate/5 border border-slate/20 hover:border-accent/50 hover:bg-accent/5 rounded-xl text-fg-muted hover:text-accent transition-colors"
          >
            <Fa icon={faArrowUpRightFromSquare} class="text-xs opacity-50" />
            <span class="truncate max-w-48">{link.title || getDisplayUrl(link.url)}</span>
          </a>
        {/each}
      </div>
    {/if}

    <div
      class="flex flex-col md:flex-row mt-4 space-y-10 md:space-y-0 md:space-x-10"
    >
      <div class="w-full md:w-96">
        <Ingredients bind:ingredients />
      </div>

      <div class="w-full">
        <Instructions bind:instructions />

        <div class="mt-6 w-full">
          <Notes bind:notes bind:rating />
        </div>
      </div>
    </div>
  </div>

  <div class="mt-4 md:mt-0 md:w-3/12">
    <Gallery {gallery} />
    <ImageUpload server={`${id}/upload_image`} />
  </div>
</main>

<style>
  .date-input {
    @apply bg-transparent border-b border-transparent text-sm text-fg  p-0;
    @apply cursor-pointer outline-none;
    @apply hover:border-border;
    @apply focus:border-accent focus:text-fg;
    -moz-appearance: textfield;
  }
  .servings-input {
    @apply bg-transparent border-b border-transparent text-sm text-fg w-14 p-0;
    @apply cursor-pointer outline-none;
    @apply hover:border-border;
    @apply focus:border-accent focus:text-fg;
    -moz-appearance: textfield;
  }

  .servings-input::-webkit-outer-spin-button,
  .servings-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .servings-input::placeholder {
    @apply text-fg-muted/40;
  }
</style>
